import { ChangeEvent, SyntheticEvent, useMemo, useState } from 'react';
import { ISearch, useApiQuery, useDebounce, useResponsive, useToogle } from '@shared';
import { SearchService, useSession } from 'App';
import { normalizeSearch, tabs } from '../helpers';

export const useSearch = () => {
  const isMobile = useResponsive({ query: 'down', key: 'sm' });
  const { isOpen, onOpen, onClose } = useToogle();

  const { currentUser } = useSession();
  const { isLeadAuditor } = currentUser.roles;

  const [offset, setOffset] = useState<number>(1);
  const [isFlagOfInterest, setIsFlagOfInterest] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearch = useDebounce(searchQuery, 700, () => setOffset(1));

  const [isNoFound, setIsNoFound] = useState<boolean>(false);

  const [tabIndex, setTabIndex] = useState<number>(0);

  const handleChangeTab = (_: SyntheticEvent, newValue: number) => {
    setOffset(1);
    setTabIndex(newValue);
    setIsNoFound(data ? !data?.[`${tabs[newValue]}`].length : false);
  };

  const { data, isInitialLoading, isFetching } = useApiQuery(
    ['search', debouncedSearch, offset - 1, tabs[tabIndex], isFlagOfInterest],
    () =>
      SearchService.getCollection({
        search_string: debouncedSearch,
        search_by: tabs[tabIndex],
        offset: offset - 1,
        audit_of_interest: isFlagOfInterest,
      }),
    {
      cacheTime: 0,
      enabled: debouncedSearch.length > 2,
      onSuccess: (data) => {
        setIsNoFound(!data?.[`${tabs[tabIndex]}`].length);
      },
    }
  );

  const output: ISearch = useMemo(() => {
    if (!data) {
      return {
        users: [],
        courses: [],
        exams: [],
        audits: [],
        coursesCount: 0,
        examsCount: 0,
        usersCount: 0,
        auditsCount: 0,
      };
    }
    return normalizeSearch(data);
  }, [data]);

  const handleOpenModalWithText = () => searchQuery?.trim() && onOpen();
  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(value);
    if (value.trim()) onOpen();
  };
  return {
    tabIndex,
    handleChangeTab,
    output,
    isOpen,
    onOpen,
    onClose,
    searchQuery,
    debouncedSearch,
    isMobile,
    handleOpenModalWithText,
    handleChange,
    isInitLoading: isInitialLoading,
    isFetching,
    isNoFound,
    offset,
    setOffset,
    isFlagOfInterest,
    setIsFlagOfInterest,
    isLeadAuditor,
  };
};
